import { useEffect } from 'react';
import type { Position } from '../types';
import { trackEvent } from '../lib/analytics';
import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { OdsBadge } from './orderly/OdsBadge';

function formatQty(val: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(val);
}

function formatUSD(val: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val);
}

function formatMarginMode(mode: Position['marginMode']): string {
  return mode === 'ISOLATED' ? 'ISO' : 'Cross';
}

interface Props {
  positions: Position[];
}

export default function PositionTable({ positions }: Props) {
  useEffect(() => {
    trackEvent('component_viewed', {
      component: 'PositionTable',
      status: positions.length,
    });
  }, [positions.length]);

  if (positions.length === 0) {
    return <div className="rounded-lg border border-dashed border-[var(--border-subtle)] p-8 text-center text-sm text-[var(--fg-secondary)]">No open positions</div>;
  }

  return (
    <Table className="min-w-[640px]">
      <TableHeader className="bg-[var(--bg-surface-secondary)]">
        <TableRow className="border-[var(--border-subtle)] hover:bg-transparent">
          <TableHead className="text-[var(--fg-secondary)]">Symbol</TableHead>
          <TableHead className="text-[var(--fg-secondary)]">Margin Mode</TableHead>
          <TableHead className="text-[var(--fg-secondary)]">Side</TableHead>
          <TableHead className="text-right text-[var(--fg-secondary)]">Quantity</TableHead>
          <TableHead className="text-right text-[var(--fg-secondary)]">Unrealized PnL</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {positions.map((pos) => (
          <TableRow key={`${pos.accountId}-${pos.symbol}-${pos.marginMode}`} className="border-[var(--border-subtle)] hover:bg-[var(--bg-hover)]">
            <TableCell className="font-semibold text-[var(--fg-base)]">{pos.symbol.replace('PERP_', '').replace('_USDC', '')}</TableCell>
            <TableCell>
              <OdsBadge type={pos.marginMode === 'ISOLATED' ? 'warning' : 'subtle'}>{formatMarginMode(pos.marginMode)}</OdsBadge>
            </TableCell>
            <TableCell>
              <OdsBadge type={pos.side.toLowerCase() === 'long' ? 'positive' : 'negative'}>{pos.side}</OdsBadge>
            </TableCell>
            <TableCell className="text-right text-[var(--fg-base)]">{formatQty(pos.quantity)}</TableCell>
            <TableCell className={cn('text-right font-medium', pos.unrealizedPnl >= 0 ? 'text-[var(--fg-positive)]' : 'text-[var(--fg-negative)]')}>
              {pos.unrealizedPnl >= 0 ? '+' : ''}{formatUSD(pos.unrealizedPnl)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
